import { LogLevel } from '../diagnostics/log-level';
import { LogRecord } from '../diagnostics/log-record';
import { Logging } from '../diagnostics/logging';
import { Dom } from '../dom/dom';

/**
 * Class for accessibility manager
 */
export class AccessibilityManager {
    /**
     * Indicates that body focus class handlers have already been setup and should not be setup again
     */
    private hiddenFocusHandlersInitialized = false;

    /**
     * The set of events for element focusing.
     */
    private elementFocusingEvents: ((event: ElementFocusingEvent) => void)[] = [];

    /**
     * The CSS class to disable the focus rectangle even in keyboard mode
     */
    public hiddenFocusClass = 'sme-hidden-focus';

    /**
     * The CSS class to enable mouse specific accessibility styles
     */
    public mouseNavigationModeClass = 'sme-accessibility-mode-mouse';

    /**
     * The CSS class to enable keyboard specific accessibility styles
     */
    public keyboardNavigationModeClass = 'sme-accessibility-mode-keyboard';

    /**
     * The set of elements that have had the hiddenFocusClass applied
     */
    public hiddenFocusElements: HTMLElement[] = [];

    /**
     * The object of ctrl+alt+a key.
     */
    private ctrlAltAShortKey: KeyResolver;

    constructor() {
        // hookup global handlers
        this.hookupGlobalHandlers();
        this.ctrlAltAShortKey = new KeyResolver('Ctrl+Alt+A');
    }

    /**
     * Registers the event handler for ElementFocusingEvent
     */
    public registerElementFocusingEvent(handler: (event: ElementFocusingEvent) => void): () => void {
        const unregisterEventFunction = () => {
            const index = this.elementFocusingEvents.indexOf(handler);
            if (index !== -1) {
                this.elementFocusingEvents.splice(index, 1);
            }
        };
        this.elementFocusingEvents.push(handler);

        return unregisterEventFunction;
    }

    /**
     * focus on given element and prevent the default of the event
     * @param element the element to focus on
     * @param event the event that triggered the focus
     * @param allowBrowserFocusHandling it indicates whether to allow browser to handle focus.
     */
    private focusOnElement(element: HTMLElement, event: KeyboardEvent, allowBrowserFocusHandling?: boolean) {
        if (element) {
            // this change needs to be taken in any snap-in implementations
            // use param {preventScroll: true} if element is in an extension iframe
            // TODO: try below code with snap-ins that use new snap-in listener code
            element.focus();
            if (!allowBrowserFocusHandling) {
                event.preventDefault();
            }
        }
    }

    /**
     * Find the first focusable descendant of an element and focus on it
     * @param element the element to work with
     * @param event the event that triggered the focus
     */
    private focusOnFirstFocusableDescendant(element: HTMLElement, event: KeyboardEvent) {
        const firstFocusableDescendant = Dom.getFirstFocusableDescendent(element);
        this.focusOnElement(firstFocusableDescendant, event);
    }

    /**
     * Handlers the element focusing in either the default way or custom ways based on ElementFocusingEvent handler.
     */
    public processElementFocusing(event: KeyboardEvent, elementToFocus: HTMLElement,
        sourceZone: HTMLElement, targetZone: HTMLElement, allowBrowserFocusHandling?: boolean) {
        let useCustomFocusHandling = false;
        let preventDefaultEvent = false;
        this.elementFocusingEvents.forEach(focusEvent => {
            focusEvent({
                nativeEvent: event,
                sourceZone: sourceZone,
                targetZone: targetZone,
                targetElement: elementToFocus,
                preventDefaultFocusBehavior: () => {
                    useCustomFocusHandling = true;
                },
                preventDefaultEvent: () => {
                    preventDefaultEvent = true;
                }
            });
        });
        if (useCustomFocusHandling) {
            if (preventDefaultEvent) {
                event.stopPropagation();
                event.preventDefault();
            }
        } else {
            this.focusOnElement(elementToFocus, event, allowBrowserFocusHandling);
        }
    }

    /**
     * click on given element and prevent the default of the event
     * @param element the element to click
     * @param event the event that triggered the click
     */
    private clickOnElement(element: HTMLElement, event: KeyboardEvent) {
        if (element) {
            element.click();
            event.preventDefault();
        }
    }

    /**
     * Changes the Accessibility Mode to mouse or keyboard
     * @param keyboardMode indicates that keyboard mode should be set
     */
    public changeAccessibilityMode(keyboardMode: boolean) {
        // toggle accessibility mode across all iframes in the document
        // only works for same origin iframes
        // TODO: support cross origin iframes and replace this with RPC broadcasting to all iframes
        const allBodys = Dom.getAllBodys();

        for (let i = 0; i < allBodys.length; i++) {
            const currentBody = allBodys[i];
            currentBody.classList.toggle(this.mouseNavigationModeClass, !keyboardMode);
            currentBody.classList.toggle(this.keyboardNavigationModeClass, keyboardMode);
        }

        // register accessibility mode with self so RPC can use it
        const self = MsftSme.self();
        self.Resources.accessibilityMode = keyboardMode;
    }

    /**
     * Query the accessibility mode of parent
     */
    private queryAccessibilityMode(): boolean {
        return !MsftSme.isNullOrUndefined(
            Dom.getSpecificAncestor(
                document.body,
                (x: HTMLElement) => Dom.isBody(x) && x.classList.contains(this.keyboardNavigationModeClass)
            )
        );
    }

    /**
     * Hooks up the global event handlers
     */
    private hookupGlobalHandlers() {
        // hookup body focus class handlers.
        // We do not need to unhook these as they last the entire applications lifecycle.
        if (!this.hiddenFocusHandlersInitialized) {
            // ensure this is only called once
            this.hiddenFocusHandlersInitialized = true;
            // apply the mouse navigation class to the body of the document as default
            this.changeAccessibilityMode(false);

            // when the user clicks on the page, we need to exit keyboard mode and enter mouse mode again
            document.body.addEventListener('mousedown', (event) => {
                // If event.buttons is 0, it means this mouse action is triggered by narrator.
                // If event.buttons is greater than 0, it means this mouse action is triggered by actual mouse device.
                // Then exit accessibility mode.
                if (event.buttons) {
                    this.changeAccessibilityMode(false);
                    setTimeout(() => Dom.checkActiveTab(), 0);
                }
            });
            document.body.addEventListener('keydown', (event) => {
                const isKeyboardMode = this.queryAccessibilityMode();
                this.changeAccessibilityMode(isKeyboardMode);
                const currentElement = <HTMLElement>event.target;
                const currentElementProperties = Dom.getElementProperties(currentElement);
                const currentTrap = currentElementProperties.currentTrap;
                const allowCustomArrowKeyFunctionality =
                    Dom.allowCustomArrowKeyFunctionality(currentElementProperties);
                const allowCustomHomeEndKeyFunctionality =
                    Dom.allowCustomHomeEndKeyFunctionality(currentElement, currentElementProperties);
                const currentZone = currentElementProperties.currentZone;
                const keyCode = event.keyCode;

                setTimeout(() => Dom.checkActiveTab(), 0);

                if (event.shiftKey && keyCode === KeyCode.Tab) {
                    // shift tab - go back to previous zone
                    let focusOn = Dom.getPreviousZoneElement(currentElement);
                    const targetZone = Dom.getAncestorZone(focusOn);

                    if (Dom.isTablist(targetZone)) {
                        focusOn = Dom.getFirstActiveOrSelectedDescendant(targetZone) || focusOn;
                    }
                    if (!currentElementProperties.withinTrap || Dom.getAncestorTrap(targetZone) === currentElementProperties.currentTrap) {
                        this.processElementFocusing(event, focusOn, currentZone, targetZone);
                    } else {
                        event.preventDefault();
                    }
                    // else we are at the beginning of the page and want shift tab to perform its default action

                } else if (keyCode === KeyCode.Tab) {
                    // when the user presses 'tab' we will enter keyboard mode
                    this.changeAccessibilityMode(true);

                    const focusOn = Dom.getNextZoneElement(currentElement);
                    const targetZone = Dom.getAncestorZone(focusOn);

                    if (focusOn && targetZone !== currentZone) {
                        // go to next zone
                        let newFocusOn = focusOn;
                        if (Dom.isTablist(targetZone)) {
                            newFocusOn = Dom.getFirstActiveOrSelectedDescendant(targetZone);
                        }
                        if (!currentElementProperties.withinTrap
                            || Dom.getAncestorTrap(targetZone) === currentElementProperties.currentTrap) {
                            this.processElementFocusing(event, newFocusOn, currentZone, targetZone);
                        } else {
                            // we are at the end of the trap so go back to the beginning of the trap
                            this.focusOnFirstFocusableDescendant(currentTrap, event);
                            event.preventDefault();
                        }
                    } else if (!currentElementProperties.withinTrap) {
                        // else we are at the end of the page and want tab to perform its default action
                        const lastElement = Dom.getLastElementInZone(currentElement);
                        if (lastElement) {
                            this.processElementFocusing(event, lastElement, currentZone, targetZone, true);
                        }
                    } else {
                        // we are at the end of the trap so go back to the beginning of the trap
                        this.focusOnFirstFocusableDescendant(currentTrap, event);
                    }

                } else if (keyCode === KeyCode.RightArrow && allowCustomArrowKeyFunctionality) {
                    // use default if the cursor is in the middle of search box text
                    const useArrowKeys = Dom.useArrowKeysWithinSearchbox(currentElement, true);
                    if (!useArrowKeys && isKeyboardMode) {
                        // go to next focusable element within current zone
                        this.focusOnElement(Dom.getNextFocusableElement(currentElement), event);
                    }
                } else if (keyCode === KeyCode.DownArrow && allowCustomArrowKeyFunctionality) {
                    // go to next focusable element within current zone
                    this.focusOnElement(Dom.getNextFocusableElement(currentElement), event);
                } else if (keyCode === KeyCode.UpArrow && allowCustomArrowKeyFunctionality) {
                    // go to previous focusable element within current zone
                    this.focusOnElement(Dom.getPreviousFocusableElement(currentElement), event);
                } else if (event.keyCode === KeyCode.LeftArrow && allowCustomArrowKeyFunctionality) {
                    // use default if the cursor is in the middle of search box text
                    const useArrowKeys = Dom.useArrowKeysWithinSearchbox(currentElement, false);
                    if (!useArrowKeys && isKeyboardMode) {
                        // go to previous focusable element within current zone
                        this.focusOnElement(Dom.getPreviousFocusableElement(currentElement), event);
                    }
                } else if (event.keyCode === KeyCode.Enter) {
                    if (document.body.classList.contains(this.keyboardNavigationModeClass)) {
                        if ((currentZone || currentElementProperties.isZone)
                            && (!currentElementProperties.withinForm || Dom.shouldTreatEnterAsClick(currentElement))) {
                            this.clickOnElement(currentElement, event);
                        }
                    }
                } else if (event.keyCode === KeyCode.End && allowCustomHomeEndKeyFunctionality) {
                    this.focusOnElement(Dom.getLastElementInZone(currentElement), event);
                } else if (event.keyCode === KeyCode.Home && allowCustomHomeEndKeyFunctionality) {
                    this.focusOnElement(Dom.getFirstElementInZone(currentElement), event);
                } else if (this.ctrlAltAShortKey.matchesWith(event)) {
                    let targetElement: HTMLElement;
                    if (Dom.isInActionBar(currentElement)) {
                        targetElement = Dom.getNextActionBar(currentElement);
                    } else {
                        targetElement = Dom.getFirstActionBar(currentElement);
                    }

                    if (!MsftSme.isNullOrUndefined(targetElement)) {
                        this.changeAccessibilityMode(true);
                        this.focusOnElement(targetElement, event, false);
                    }
                }
            });
        }
    }
}

/**
 * Class to resolve keys.
 */
export class KeyResolver {

    /**
     * Whether has shift key.
     */
    public hasShiftKey = false;

    /**
     * Whether has alt key.
     */
    public hasAltKey = false;

    /**
     * Whether has ctrl key.
     */
    public hasCtrlKey = false;

    /**
     * The main key code for key combination.
     */
    public keyCode: KeyCode;

    /**
     * Initializes an instance of KeyLocalizer
     * @param inputkeys Input keys.
     */
    constructor(inputkeys: string) {
        if (!MsftSme.isNullOrWhiteSpace(inputkeys)) {
            this.resolveKeys(inputkeys);
        }
    }

    /**
     * Resolves localized keys in to this class structure.
     * @param inputKeys Input keys.
     */
    private resolveKeys(inputKeys: string) {

        if (inputKeys === '+') {
            this.keyCode = KeyCode.Add;
            return;
        }
        const keys = inputKeys.split('+');
        if (keys.length > 0 && keys.length === 1) {
            this.setModifierKeyFlags(keys[0]);
            this.setKeycode(keys[0]);
        } else {
            for (let i = 0; i < keys.length - 1; i++) {
                this.setModifierKeyFlags(keys[i]);
            }
            this.setKeycode(keys[keys.length - 1]);
        }
    }

    /**
     * Sets modifier key flags if found in key combinations.
     * @param key The key.
     */
    private setModifierKeyFlags(key: string) {

        // These should always be passed as english keys.
        if (key === 'Ctrl') {
            this.hasCtrlKey = true;
        } else if (key === 'Alt') {
            this.hasAltKey = true;
        } else if (key === 'Shift') {
            this.hasShiftKey = true;
        }
    }

    /**
     * Set the key code extracted from the input key.
     * @param key the key string.
     */
    private setKeycode(key: string) {
        const keyCode: KeyCode = (<any>KeyCode)[key];
        this.keyCode = keyCode;
        if (this.keyCode === undefined) {
            Logging.log(<LogRecord>{
                level: LogLevel.Error,
                message: 'Could not resolve key ' + key,
                source: KeyResolver.name
            });
        }

    }

    /**
     * Checks if keyboard event matches with resolved keys.
     * @param event The keyboard event containing pressed key information.
     */
    public matchesWith(event: KeyboardEvent): boolean {
        return this.hasCtrlKey === event.ctrlKey &&
            this.hasAltKey === event.altKey &&
            this.hasShiftKey === event.shiftKey &&
            this.keyCode === event.keyCode;
    }
}

/**
 * Interface for ElementFocusingEvent.
 */
export interface ElementFocusingEvent {
    /**
     * It indicates the source focus zone.
     */
    sourceZone: HTMLElement;

    /**
     * It indicates the target focus zone.
     */
    targetZone: HTMLElement;

    /**
     * It indicates the target focus element.
     */
    targetElement: HTMLElement;

    /**
     * If this function is called in the event handler, it cancels the default behavior of focusing
     */
    preventDefaultFocusBehavior: () => void;

    /**
     * If this function is called in the event handler, it cancels the default event of focusing
     */
    preventDefaultEvent: () => void;

    nativeEvent: KeyboardEvent;
}

// Keyboard codes
export enum KeyCode {
    Backspace = 8,
    Tab = 9,
    Enter = 13,
    Shift = 16,
    Ctrl = 17,
    Alt = 18,
    Pause = 19,
    CapsLock = 20,
    Escape = 27,
    Space = 32,
    PageUp = 33,
    PageDown = 34,
    End = 35,
    Home = 36,
    LeftArrow = 37,
    UpArrow = 38,
    RightArrow = 39,
    DownArrow = 40,
    Insert = 45,
    Delete = 46,
    Num0 = 48,
    Num1 = 49,
    Num2 = 50,
    Num3 = 51,
    Num4 = 52,
    Num5 = 53,
    Num6 = 54,
    Num7 = 55,
    Num8 = 56,
    Num9 = 57,
    A = 65,
    B = 66,
    C = 67,
    D = 68,
    E = 69,
    F = 70,
    G = 71,
    H = 72,
    I = 73,
    J = 74,
    K = 75,
    L = 76,
    M = 77,
    N = 78,
    O = 79,
    P = 80,
    Q = 81,
    R = 82,
    S = 83,
    T = 84,
    U = 85,
    V = 86,
    W = 87,
    X = 88,
    Y = 89,
    Z = 90,
    LeftWindows = 91,
    RightWindows = 92,
    Select = 93,
    Numpad0 = 96,
    Numpad1 = 97,
    Numpad2 = 98,
    Numpad3 = 99,
    Numpad4 = 100,
    Numpad5 = 101,
    Numpad6 = 102,
    Numpad7 = 103,
    Numpad8 = 104,
    Numpad9 = 105,
    Multiply = 106,
    Add = 107,
    Subtract = 109,
    DecimaPoint = 110,
    Divide = 111,
    F1 = 112,
    F2 = 113,
    F3 = 114,
    F4 = 115,
    F5 = 116,
    F6 = 117,
    F7 = 118,
    F8 = 119,
    F9 = 120,
    F10 = 121,
    F11 = 122,
    F12 = 123,
    NumLock = 144,
    ScrollLock = 145,
    SemiColon = 186,
    EqualSign = 187,
    Comma = 188,
    Dash = 189,
    Period = 190,
    ForwardSlash = 191,
    GraveAccent = 192,
    OpenBracket = 219,
    BackSlash = 220,
    CloseBraket = 221,
    SingleQuote = 222
}
